import React, { useState } from'react';
import { Share2, Facebook, Twitter, Linkedin, Link2, MessageCircle, Check, X } from'lucide-react';
import { Button } from'./ui/button';
import { toast } from'sonner';

export default function ShareButtons({ title, description, url, type ='club' }) {
 const [isOpen, setIsOpen] = useState(false);
 const [copied, setCopied] = useState(false);

 const shareUrl = url || window.location.href;
 const shareTitle = title || document.title;
 const shareText = description || `Check out this ${type} on Line Dance Central!`;

 const shareLinks = [
 {
 name:'Facebook',
 icon: Facebook,
 color:'hover:bg-blue-600 hover:text-white',
 url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`
 },
 {
 name:'Twitter',
 icon: Twitter,
 color:'hover:bg-sky-500 hover:text-white',
 url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`
 },
 {
 name:'LinkedIn',
 icon: Linkedin,
 color:'hover:bg-blue-700 hover:text-white',
 url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
 },
 {
 name:'WhatsApp',
 icon: MessageCircle,
 color:'hover:bg-green-500 hover:text-white',
 url: `https://wa.me/?text=${encodeURIComponent(`${shareTitle} - ${shareUrl}`)}`
 }
 ];

 const copyToClipboard = async () => {
 try {
 await navigator.clipboard.writeText(shareUrl);
 setCopied(true);
 toast.success('Link copied to clipboard!');
 setTimeout(() => setCopied(false), 2000);
 } catch (err) {
 toast.error('Failed to copy link');
 }
 };

 const handleShare = (link) => {
 window.open(link.url,'_blank','width=600,height=400,noopener,noreferrer');
 };

 // Use native share if available on mobile
 const handleNativeShare = async () => {
 if (navigator.share) {
 try {
 await navigator.share({
 title: shareTitle,
 text: shareText,
 url: shareUrl
 });
 } catch (err) {
 if (err.name !=='AbortError') {
 setIsOpen(true);
 }
 }
 } else {
 setIsOpen(!isOpen);
 }
 };

 return (
 <div className="relative">
 <Button
 variant="secondary"
 size="sm"
 onClick={handleNativeShare}
 className="gap-2 bg-white shadow-md border border-slate-200 text-slate-700 hover:bg-slate-50"
 data-testid="share-button"
 >
 <Share2 className="w-4 h-4" />
 <span className="hidden sm:inline">Share</span>
 </Button>

 {isOpen && (
 <>
 {/* Backdrop */}
 <div 
 className="fixed inset-0 z-40" 
 onClick={() => setIsOpen(false)}
 />
 
 {/* Share menu */}
 <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
 <div className="p-3 border-b border-slate-100 flex items-center justify-between">
 <span className="font-semibold text-slate-900 text-sm">Share this {type}</span>
 <button 
 onClick={() => setIsOpen(false)}
 className="text-slate-400 hover:text-slate-600"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 
 <div className="p-2">
 {shareLinks.map((link) => (
 <button
 key={link.name}
 onClick={() => handleShare(link)}
 className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 transition-colors ${link.color}`}
 data-testid={`share-${link.name.toLowerCase()}`}
 >
 <link.icon className="w-5 h-5" />
 <span className="text-sm font-medium">{link.name}</span>
 </button>
 ))}
 
 <div className="border-t border-slate-100 my-2" />
 
 <button
 onClick={copyToClipboard}
 className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-violet-100 hover:text-violet-700 transition-colors"
 data-testid="share-copy-link"
 >
 {copied ? (
 <>
 <Check className="w-5 h-5 text-green-500" />
 <span className="text-sm font-medium text-green-500">Copied!</span>
 </>
 ) : (
 <>
 <Link2 className="w-5 h-5" />
 <span className="text-sm font-medium">Copy Link</span>
 </>
 )}
 </button>
 </div>
 </div>
 </>
 )}
 </div>
 );
}
